// Public clip landing page.
//
// Coaches send recap emails with https://coachquick.com/c/v/<token>. Instead
// of immediately redirecting to the underlying video file, we render a tiny
// page with the video player AND a prominent "Download this clip" button —
// because uploaded clips are auto-deleted from our server after 90 days, we
// actively want clients to save their own copy.
import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { Download, Play, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const SIGNED_URL_TTL_SECONDS = 60 * 15; // 15 minutes — minted fresh per page load.

type ResolvedClip =
  | { kind: "link"; externalUrl: string; caption: string | null }
  | {
      kind: "upload";
      playUrl: string;
      downloadUrl: string;
      caption: string | null;
    };

const resolveClip = createServerFn({ method: "GET" })
  .inputValidator((data: { token: string }) => data)
  .handler(async ({ data }): Promise<ResolvedClip | null> => {
    const token = data.token;
    if (!token || token.length < 6) return null;

    const { createClient } = await import("@supabase/supabase-js");
    const supabasePublic = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      {
        auth: {
          storage: undefined,
          persistSession: false,
          autoRefreshToken: false,
        },
      },
    );

    const { data: clip, error } = await supabasePublic.rpc(
      "resolve_clip_share" as never,
      { _token: token } as never,
    );
    if (error || !clip) return null;

    const c = clip as {
      source: "upload" | "link";
      storage_path: string | null;
      external_url: string | null;
      caption: string | null;
    };

    if (c.source === "link" && c.external_url) {
      return { kind: "link", externalUrl: c.external_url, caption: c.caption };
    }

    if (c.source === "upload" && c.storage_path) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const [{ data: playSigned }, { data: dlSigned }] = await Promise.all([
        supabaseAdmin.storage
          .from("session-clips")
          .createSignedUrl(c.storage_path, SIGNED_URL_TTL_SECONDS),
        supabaseAdmin.storage
          .from("session-clips")
          .createSignedUrl(c.storage_path, SIGNED_URL_TTL_SECONDS, {
            download: filenameFromPath(c.storage_path),
          }),
      ]);
      if (!playSigned?.signedUrl || !dlSigned?.signedUrl) return null;
      return {
        kind: "upload",
        playUrl: playSigned.signedUrl,
        downloadUrl: dlSigned.signedUrl,
        caption: c.caption,
      };
    }

    return null;
  });

function filenameFromPath(path: string): string {
  const base = path.split("/").pop() || "clip.mp4";
  // Strip the random UUID prefix if present, keep the extension.
  const ext = base.split(".").pop() || "mp4";
  return `coach-clip.${ext}`;
}

export const Route = createFileRoute("/c/v/$token")({
  loader: async ({ params }) => {
    const clip = await resolveClip({ data: { token: params.token } });
    if (!clip) throw notFound();
    return { clip };
  },
  head: () => ({
    meta: [
      { title: "Lesson clip — CoachQuick" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  notFoundComponent: () => (
    <ShellWrap>
      <h1 className="text-xl font-semibold">Clip not found</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This clip link is no longer available. Clips are automatically removed 90 days after
        upload — please contact your coach if you need it again.
      </p>
    </ShellWrap>
  ),
  errorComponent: () => (
    <ShellWrap>
      <h1 className="text-xl font-semibold">Something went wrong</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        We couldn't load this clip right now. Please try again in a moment.
      </p>
    </ShellWrap>
  ),
  component: ClipPage,
});

function ClipPage() {
  const { clip } = Route.useLoaderData();
  const caption = clip.caption?.trim();

  return (
    <ShellWrap>
      {caption ? (
        <h1 className="mb-3 text-xl font-semibold">{caption}</h1>
      ) : (
        <h1 className="mb-3 text-xl font-semibold">Your lesson clip</h1>
      )}

      <div className="overflow-hidden rounded-lg border bg-black">
        {clip.kind === "upload" ? (
          <video
            src={clip.playUrl}
            controls
            playsInline
            className="aspect-video w-full bg-black"
          />
        ) : (
          <a
            href={clip.externalUrl}
            target="_blank"
            rel="noreferrer"
            className="flex aspect-video w-full items-center justify-center bg-muted text-foreground"
          >
            <span className="flex items-center gap-2 rounded-full bg-background/90 px-4 py-2 text-sm font-medium shadow">
              <Play className="h-4 w-4" fill="currentColor" /> Open video
            </span>
          </a>
        )}
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        {clip.kind === "upload" ? (
          <Button asChild size="lg" className="w-full sm:w-auto">
            <a href={clip.downloadUrl}>
              <Download className="mr-2 h-5 w-5" /> Download this clip
            </a>
          </Button>
        ) : (
          <Button asChild size="lg" className="w-full sm:w-auto">
            <a href={clip.externalUrl} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-2 h-5 w-5" /> Open original video
            </a>
          </Button>
        )}
      </div>

      <p className="mt-6 rounded-md border border-dashed bg-muted/40 p-3 text-sm text-muted-foreground">
        {clip.kind === "upload" ? (
          <>
            <strong className="text-foreground">Save this to your phone or computer.</strong>{" "}
            Your coach's clips are automatically removed from CoachQuick 90 days after upload,
            so download anything you'd like to keep.
          </>
        ) : (
          <>This clip lives on the original video site — it will stay available there.</>
        )}
      </p>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        Shared via <span className="font-semibold">CoachQuick</span>
      </p>
    </ShellWrap>
  );
}

function ShellWrap({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto min-h-screen w-full max-w-2xl px-4 py-8 sm:py-12">
      {children}
    </main>
  );
}
